import dayjs from "dayjs";
import { parsePhoneNumber } from "react-phone-number-input";

export const emailValid = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const fullPhoneNumberToNumberAndCountryCode = (phoneNumber) => {
  if (!phoneNumber) {
    return null;
  }

  const parsedPhoneNumber = parsePhoneNumber(phoneNumber);

  if (parsedPhoneNumber == null) {
    return null;
  }

  const gsm = parsedPhoneNumber.formatNational().replaceAll(" ", "").trim();
  const countryCode = parsedPhoneNumber.countryCallingCode;

  return { gsm, countryCode };
};

export const numberAndCountryCodeToFullPhoneNumber = (
  phoneNumber,
  countryCode,
) => {
  const hasCountryCode = phoneNumber?.startsWith("+");

  if (!phoneNumber || (!hasCountryCode && !countryCode)) {
    return "";
  }

  let finalPhoneNumber = phoneNumber;
  if (!hasCountryCode) {
    finalPhoneNumber = `+${countryCode}${phoneNumber}`;
  }

  const parsedNumber = parsePhoneNumber(finalPhoneNumber);
  if (parsedNumber == null) {
    return "";
  }

  return parsedNumber.number;
};

export const areTimesValid = (startTime, endTime) => {
  return startTime != null && endTime != null && startTime < endTime;
};

export const getDurationsAndPrice = (service, user) => {
  let _startDuration;
  let _timeOffDuration;
  let _endDuration;

  let _hasTimeOff = service.timeOffStart != 0 && service.timeOffDuration != 0;

  if (user) {
    _hasTimeOff = user.timeoffDuration != 0;

    if (_hasTimeOff) {
      _startDuration = user.timeoffStart;
      _timeOffDuration = user.timeoffDuration;
      _endDuration = user.duration - _startDuration - _timeOffDuration;
    } else {
      _startDuration = user.duration;
      _timeOffDuration = null;
      _endDuration = null;
    }
  } else {
    if (_hasTimeOff) {
      const startDuration = service.timeOffStart;
      const timeOffDuration = service.timeOffDuration;

      let endDuration = service.duration - startDuration - timeOffDuration;
      if (endDuration < 0) endDuration = 0;

      _startDuration = startDuration;
      _timeOffDuration = service.timeOffDuration;
      _endDuration = endDuration;
    } else {
      _startDuration = service.duration;
      _timeOffDuration = null;
      _endDuration = null;
    }
  }

  const _price = user?.price ?? service?.price ?? 0;

  return {
    startDuration: _startDuration,
    timeOffDuration: _timeOffDuration,
    endDuration: _endDuration,
    price: _price,
    hasTimeOff: _hasTimeOff,
  };
};

export function calculateYOffsetForTime({
  time,
  startTime,
  endTime,
  timeStep,
  slotHeight,
  date,
  timeZone,
}) {
  if (date) {
    const currentDateTime = getDateTimeInZone(date, time, timeZone);
    const startDateTime = getDateTimeInZone(date, startTime, timeZone);
    const endDateTime = getDateTimeInZone(date, endTime, timeZone);

    if (
      currentDateTime != null &&
      startDateTime != null &&
      endDateTime != null
    ) {
      const minutesFromStart = currentDateTime.diff(
        startDateTime,
        "minutes",
        true,
      );
      const minutesBetweenStartAndEnd = endDateTime.diff(
        startDateTime,
        "minutes",
        true,
      );

      if (minutesFromStart < 0) {
        return 0;
      }

      if (minutesFromStart > minutesBetweenStartAndEnd) {
        return (minutesBetweenStartAndEnd / timeStep) * slotHeight;
      }

      return (minutesFromStart / timeStep) * slotHeight;
    }
  }

  const timeSplit = time.split(":");
  const hour = parseInt(timeSplit[0]);
  const minute = parseInt(timeSplit[1]);

  const startSplit = startTime.split(":");
  const startHour = parseInt(startSplit[0]);
  const startMinute = parseInt(startSplit[1]);

  const minutesFromStart = (hour - startHour) * 60 + (minute - startMinute);

  const endSplit = endTime.split(":");
  const endHour = parseInt(endSplit[0]);
  const endMinute = parseInt(endSplit[1]);

  const minutesFromEnd = (endHour - hour) * 60 + (endMinute - minute);

  // If the time is before the start time, return 0
  if (minutesFromStart < 0) {
    return 0;
  }

  // If the time is after the end time, return the height of the last slot
  if (minutesFromEnd < 0) {
    const minutesBetweenStartAndEnd =
      (endHour - startHour) * 60 + (endMinute - startMinute);

    return (minutesBetweenStartAndEnd / timeStep) * slotHeight;
  }

  const rowIndex = minutesFromStart / timeStep;

  return rowIndex * slotHeight;
}

function getDateTimeInZone(date, time, timeZone) {
  const dateTime = `${date} ${time}`;
  const format = "YYYY-MM-DD HH:mm";

  if (timeZone) {
    return dayjs.tz(dateTime, format, timeZone);
  }

  return dayjs(dateTime, format);
}
